import StoreTags from '@enums/StoreTags'
import {
  MyCourse,
  MyCourseDetailed,
  MyCoursesQueryParams,
  CreateMyCourseBody,
  UpdateMyCourseBody,
} from '@models/myCourse'
import { ItemsFromResponse } from '@models/common'
import { createDownloadExcelReportQuery } from '@utils/files'
import { HYDRA_MEMBER, HYDRA_TOTAL_ITEMS } from '../../config/consts'
import emptySplitApi from '../api'

const BASE_URL = '/api/my_courses'
const COURSES_URL = '/api/courses'

export const myCoursesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getMyCourses: builder.query<
      { items: MyCourse[]; length: number },
      MyCoursesQueryParams
    >({
      providesTags: [StoreTags.MY_COURSES],
      query: (params: MyCoursesQueryParams) => ({
        url: BASE_URL,
        params,
      }),
      transformResponse: (response: ItemsFromResponse<MyCourse>) => ({
        items: response[HYDRA_MEMBER],
        length: response[HYDRA_TOTAL_ITEMS],
      }),
    }),
    getMyCourseById: builder.query<MyCourseDetailed, number | string>({
      providesTags: [StoreTags.MY_COURSE],
      query: (id: number | string) => `${BASE_URL}/${id}`,
    }),
    postMyCourse: builder.mutation<MyCourseDetailed, CreateMyCourseBody>({
      invalidatesTags: (result: MyCourseDetailed | undefined) =>
        result ? [StoreTags.MY_COURSES] : [],
      query: (body: CreateMyCourseBody) => ({
        url: COURSES_URL,
        method: 'POST',
        body,
      }),
    }),
    putMyCourse: builder.mutation<MyCourseDetailed, UpdateMyCourseBody>({
      invalidatesTags: (result: MyCourseDetailed | undefined) =>
        result ? [StoreTags.MY_COURSES, StoreTags.MY_COURSE] : [],
      query: ({ id, ...body }: UpdateMyCourseBody) => ({
        url: `${COURSES_URL}/${id}`,
        method: 'PUT',
        body,
      }),
    }),
    publishMyCourse: builder.mutation<
      MyCourseDetailed,
      { id: number; published: boolean }
    >({
      invalidatesTags: (result: MyCourseDetailed | undefined) =>
        result ? [StoreTags.MY_COURSES, StoreTags.MY_COURSE] : [],
      query: ({ id, published }: { id: number; published: boolean }) => ({
        url: `${COURSES_URL}/${id}`,
        method: 'PATCH',
        body: { published },
      }),
    }),
    deleteMyCourse: builder.mutation<void, number>({
      invalidatesTags: [StoreTags.MY_COURSES, StoreTags.MY_COURSE],
      query: (id: number) => ({
        url: `${COURSES_URL}/${id}`,
        method: 'DELETE',
      }),
    }),
    restoreMyCourse: builder.mutation<MyCourseDetailed, number>({
      invalidatesTags: [StoreTags.MY_COURSES, StoreTags.MY_COURSE],
      query: (id: number) => ({
        url: `${COURSES_URL}/${id}/restore`,
        method: 'PUT',
        body: {},
      }),
    }),
    getCompletionReport: builder.query<void, void>({
      query: () =>
        createDownloadExcelReportQuery(
          '/api/reports/employee_education_destinations/completion',
          'completion_report.xlsx'
        )({}),
    }),
  }),
})

export const {
  useGetMyCoursesQuery,
  useGetMyCourseByIdQuery,
  useLazyGetMyCourseByIdQuery,
  usePostMyCourseMutation,
  usePutMyCourseMutation,
  usePublishMyCourseMutation,
  useDeleteMyCourseMutation,
  useRestoreMyCourseMutation,
  useLazyGetCompletionReportQuery,
} = myCoursesApi
